/* ============================================
   QUOTE DASHBOARD — + New Modals (Phone Lead / Sample / Pending / Email Lead)
   Asset: dashboard-lead-create.js

   Extracted from dashboard-main.js on 2026-05-21 as Phase A.3 of the
   dashboard refactor. Loaded via <script defer> in
   sections/rep-quote-dashboard.liquid BEFORE main.js.

   Public API (already window-scoped, unchanged from pre-refactor):
     window.__openNewLead(e, opts)        — Phone Lead modal (asSample opt → Sample)
     window.__openNewSample(e)            — thin wrapper around __openNewLead
     window.__openNewEmailLead(e, opts)   — Email Lead two-step flow (AI extraction → review)
     window.__openNewPending(e)           — New Pending Deal modal

   No delegation stubs needed in main.js — these were already window
   globals before the refactor. Other call sites (e.g. inline triggers
   from the dropdown menu) continue to reach them via window.__open*.

   Init contract (RQD.initLeadCreate called once by main.js at boot):
     daysFromNowYmd, snapYmdToWeekday    — pure date helpers
     API, Auth                            — defaulted from window.RepPortal if omitted
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  // Injected by RQD.initLeadCreate (from main.js boot)
  var daysFromNowYmd, snapYmdToWeekday;
  var API, Auth;
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-25) — fix: Phase A.3 extraction
     missed these deps, so all four "+ New" openers threw a strict-mode
     ReferenceError on their first modal* / pendingStatusChange access and the
     popup never appeared. setPendingStatusChange is a SETTER closure into
     main.js's `pendingStatusChange` var (the modal-footer Confirm dispatcher
     reads it) — injected as a setter because the openers REASSIGN this shared
     state and a value-injection wouldn't propagate back to main.js. */
  var setPendingStatusChange;
  /* Modal chrome elements — resolved lazily from the DOM (stable IDs in the
     section markup) on first opener call. NOT injected: the boot-time
     initLeadCreate runs before main.js assigns its own modal* vars (var-
     hoisting), so a value-injection would capture undefined. getElementById
     at click time is bulletproof. */
  var modalTitle, modalSub, modalEl, modalConfirm, modalBody, modalBackdrop;

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — Customer-history typeahead
     (same idea as the Quote Workstation's name/org autocomplete): type an org or
     name into a modal field, pick a past customer, and the rest of their contact
     auto-fills. Backed by the shared /customer-emails index, cached once. */
  var _custLookupCache = null;
  function _loadCustLookup() {
    if (_custLookupCache) return Promise.resolve(_custLookupCache);
    if (!API) return Promise.resolve([]);
    return API.get('/customer-emails')
      .then(function(d) { _custLookupCache = (d && d.emails) || []; return _custLookupCache; })
      .catch(function() { _custLookupCache = []; return _custLookupCache; });
  }
  function _ldEsc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function(c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /* Bind a typeahead to `input`, matching customers by `field` ('company' or
     'name'); on pick, call onPick(matchEntry). Inline-styled so it needs no
     extra CSS in the modal context. */
  function _bindCustomerLookup(input, field, onPick) {
    if (!input) return;
    _loadCustLookup();
    /* Wrap the input in a relatively-positioned block so the dropdown anchors
       directly beneath THIS field (org/name are bare children of the modal body,
       so anchoring to parentElement would drop the menu at the modal's bottom). */
    var anchor = document.createElement('div');
    anchor.style.cssText = 'position:relative;display:block;';
    input.parentNode.insertBefore(anchor, input);
    anchor.appendChild(input);
    var dd = document.createElement('div');
    dd.style.cssText = 'position:absolute;z-index:60;left:0;right:0;top:100%;margin-top:2px;background:var(--rq-bg,#fff);border:1px solid var(--rq-border,#e5e5e5);border-radius:6px;box-shadow:0 6px 18px rgba(0,0,0,0.14);max-height:210px;overflow:auto;display:none;';
    anchor.appendChild(dd);
    var idx = -1, cur = [];
    function close() { dd.style.display = 'none'; idx = -1; }
    function filt(q) {
      var cache = _custLookupCache || [];
      if (!q || q.length < 2) return [];
      var qq = q.toLowerCase(), seen = {}, out = [];
      for (var i = 0; i < cache.length; i++) {
        var c = cache[i]; if (!c) continue;
        var hay = field === 'company' ? (c.company || '') : (c.name || '');
        if (!hay || hay.toLowerCase().indexOf(qq) === -1) continue;
        var key = (hay + '|' + (c.email || '')).toLowerCase();
        if (seen[key]) continue; seen[key] = true;
        out.push(c); if (out.length >= 6) break;
      }
      return out;
    }
    function hi() { var items = dd.children; for (var i = 0; i < items.length; i++) items[i].style.background = (i === idx) ? 'rgba(0,107,204,0.10)' : ''; }
    function render(matches) {
      cur = matches;
      if (!matches.length) { close(); return; }
      dd.innerHTML = matches.map(function(m, i) {
        var primary = field === 'company' ? (m.company || '') : (m.name || '');
        var sub = [field === 'company' ? (m.name || '') : (m.company || ''), m.email || ''].filter(Boolean).join(' · ');
        return '<div data-i="' + i + '" style="padding:7px 10px;cursor:pointer;font-size:13px;line-height:1.3;border-bottom:1px solid var(--rq-border,#eee);">' + _ldEsc(primary) + (sub ? '<span style="opacity:0.6;font-size:11px;display:block;">' + _ldEsc(sub) + '</span>' : '') + '</div>';
      }).join('');
      dd.style.display = 'block'; idx = -1;
    }
    input.addEventListener('input', function() { render(filt(this.value.trim())); });
    input.addEventListener('blur', function() { setTimeout(close, 160); });
    dd.addEventListener('mousedown', function(e) { e.preventDefault(); var it = e.target.closest('[data-i]'); if (it) { var m = cur[parseInt(it.getAttribute('data-i'), 10)]; close(); if (m) onPick(m); } });
    /* Hover highlight (mirrors the keyboard-nav highlight). */
    dd.addEventListener('mouseover', function(e) { var it = e.target.closest('[data-i]'); if (!it) return; idx = parseInt(it.getAttribute('data-i'), 10); hi(); });
    dd.addEventListener('mouseleave', function() { idx = -1; hi(); });
    input.addEventListener('keydown', function(e) {
      if (dd.style.display !== 'block') return;
      var n = dd.children.length; if (!n) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); idx = idx < n - 1 ? idx + 1 : 0; hi(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); idx = idx > 0 ? idx - 1 : n - 1; hi(); }
      else if (e.key === 'Enter' || e.key === 'Tab') { if (idx >= 0) { e.preventDefault(); var m = cur[idx]; close(); if (m) onPick(m); } }
      else if (e.key === 'Escape') { close(); }
    });
  }
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-29) — expose so __openEditCustomer
     in dashboard-main.js can reuse the same typeahead without duplicating it. */
  window.RQD.bindCustomerLookup = _bindCustomerLookup;
  window.RQD.getCustLookupCache = function() { return _custLookupCache; };

  function _ensureModalEls() {
    if (modalEl) return;
    modalBackdrop = document.getElementById('rqd-modal-backdrop');
    modalEl       = document.getElementById('rqd-modal');
    modalTitle    = document.getElementById('rqd-modal-title');
    modalSub      = document.getElementById('rqd-modal-sub');
    modalBody     = document.getElementById('rqd-modal-body');
    modalConfirm  = document.getElementById('rqd-modal-confirm');
  }

    // ── + New Lead / + New Sample modal ── (2026-05-06 PM⁶ simplified UX:
    // single-column stacked layout matching the Pending modal feel — plain
    // labels, no section dividers, no 2-col grids, no rich-text toolbar.
    // Plain <textarea> for message. Ship To stays collapsible. Wider modal
    // preserved so the wide labels read cleanly.)
    //
    // Phone-call entry: source='manual' → standard lead pipeline.
    // Sample entry (asSample=true): source='sample-request' → SAMPLE cold pool.
    window.__openNewLead = function(e, opts) {
      if (e) e.stopPropagation();
      var asSample = !!(opts && opts.asSample);
      var menu = document.getElementById('rqd-new-menu');
      if (menu) menu.style.display = 'none';
      var sess = window.RepPortal && window.RepPortal.Auth.getSession();
      if (sess && (sess.role === 'cfa')) return;
      var isAdminUser = sess && sess.admin;
      _ensureModalEls();

      modalTitle.textContent = asSample ? 'New Sample Lead' : 'New Phone Lead';
      modalSub.textContent = asSample
        ? 'Capture a phone-in or email sample request — routes to the territory rep'
        : 'Capture a phone-call inquiry — territory routing applies';
      modalEl.classList.add('rqd-modal--wide');
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Explicit button
         reset. The Email Lead flow sets modalConfirm to "Process with
         AI"; without this reset the next New Lead / New Sample modal
         (or even step 2 of Email Lead after extraction) inherits the
         stale label. None of these three paths involve AI — just
         straight Create. */
      modalConfirm.textContent = 'Create';
      modalConfirm.disabled = false;

      var html = '';

      // Sample inquiry channel — mandatory for + New Sample. Lets us tell
      // a phone-in sample from an email inquiry without splitting the
      // visual treatment (both stay sample-themed in the dashboard).
      if (asSample) {
        html += '<label class="rqd-modal__label" style="margin-top:0;">Inquiry Channel <span style="color:#dc2626;">*</span> <span style="font-weight:400;text-transform:none;letter-spacing:0;color:var(--rq-text-secondary);">(how did this come in?)</span></label>';
        html += '<div class="rqd-sample-channel-toggle" data-sample-channel-toggle>';
        html += '<button type="button" class="rqd-sample-channel-toggle__btn" data-sample-channel="phone">📞 Phone</button>';
        html += '<button type="button" class="rqd-sample-channel-toggle__btn" data-sample-channel="email">✉ Email</button>';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — Referral channel. Picking this
           reveals the Referred By + % of GP fields below so admin can
           capture the referrer at the same time the sample lead is
           created. Worker side: this channel value flows through to
           lc.leadSource = 'shared' for commission attribution downstream. */
        html += '<button type="button" class="rqd-sample-channel-toggle__btn" data-sample-channel="referral">🤝 Referral</button>';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
        html += '</div>';

        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — Project Type + Square Feet, the two
           answers the website sample form collects and ECORE's work order prints as "Project
           Details". Ryan: *"sometimes people will bypass this form and call us up to order their
           samples… We therefore need to have an option in this form to select the stuff that would
           normally comprise the 'Project details'."* Both required (validated at submit, in main.js).
           ⛔ THE OPTION LIST IS SHARED WITH THE SAMPLES CARD — `RQD.SAMPLE_PROJECT_TYPES`, declared
           in dashboard-lead-detail.js and read here at open time, so load order does not matter and
           there is one list on the dashboard. It mirrors the storefront section's list verbatim;
           change one, change both (and the worker's copy in lib/sample-project.js). "Other" reveals
           a text box and stores as "Other — <text>", the storefront's own shape. */
        var _spTypes = (window.RQD && Array.isArray(window.RQD.SAMPLE_PROJECT_TYPES)) ? window.RQD.SAMPLE_PROJECT_TYPES : ['Other'];
        html += '<div class="rqd-modal__field-row rqd-modal__field-row--2col">';
        html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Project Type <span style="color:#dc2626;">*</span></label>'
              +   '<select class="rqd-modal__input" id="rqd-lead-project-type"><option value="">Select one</option>'
              +   _spTypes.map(function (t) { var e = String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); return '<option value="' + e + '">' + e + '</option>'; }).join('')
              +   '</select></div>';
        html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Square Feet of Area <span style="color:#dc2626;">*</span></label>'
              +   '<input type="number" class="rqd-modal__input" id="rqd-lead-square-feet" min="1" step="1" inputmode="numeric" placeholder="e.g. 2400"></div>';
        html += '</div>';
        html += '<div id="rqd-lead-project-type-other-wrap" style="display:none;">';
        html +=   '<label class="rqd-modal__label">Please specify</label>';
        html +=   '<input type="text" class="rqd-modal__input" id="rqd-lead-project-type-other" maxlength="100" placeholder="Tell us about the project">';
        html += '</div>';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      }

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Field rows
         compacted into 2-col / 3-col layouts to reclaim vertical
         space. Customer Name + Organization stay full-width (they
         can be long). Email + Phone paired (both short). Street 1 +
         Street 2 paired. Zip + City + State as a 3-col row with
         zip-first to preserve the auto-lookup workflow that pre-fills
         city + state from zip. Mobile (<600px) falls back to single
         column. */
      // Contact identity
      html += '<label class="rqd-modal__label">Customer Name</label>';
      html += '<input type="text" class="rqd-modal__input" id="rqd-lead-name" placeholder="John Smith">';
      html += '<div class="rqd-modal__field-row rqd-modal__field-row--2col">';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Email (comma separated)</label><input type="email" class="rqd-modal__input" id="rqd-lead-email" placeholder="john@example.com"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Phone</label><input type="tel" class="rqd-modal__input" id="rqd-lead-phone" placeholder="(555) 123-4567"></div>';
      html += '</div>';

      // Organization + Residential flag
      html += '<label class="rqd-modal__label">Organization</label>';
      html += '<input type="text" class="rqd-modal__input" id="rqd-lead-org" placeholder="Acme Fitness Center">';
      html += '<label style="display:flex;align-items:center;gap:8px;font-weight:600;font-size:12px;cursor:pointer;color:var(--rq-text);margin:6px 0 10px;"><input type="checkbox" id="rqd-lead-residential" style="width:auto;margin:0;cursor:pointer;"> Residential</label>';

      // Sold-to address — Street 1+2 paired, then Zip+City+State as 3-col
      html += '<div class="rqd-modal__field-row rqd-modal__field-row--2col">';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Street 1</label><input type="text" class="rqd-modal__input" id="rqd-lead-sold-street1" placeholder="123 Main St"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Street 2</label><input type="text" class="rqd-modal__input" id="rqd-lead-sold-street2" placeholder="Suite 200"></div>';
      html += '</div>';
      html += '<div class="rqd-modal__field-row rqd-modal__field-row--3col">';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Zip Code</label><input type="text" class="rqd-modal__input" id="rqd-lead-zip" placeholder="80021" maxlength="10"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">City <span style="font-weight:400;text-transform:none;letter-spacing:0;color:var(--rq-text-secondary);">(auto)</span></label><input type="text" class="rqd-modal__input" id="rqd-lead-sold-city" placeholder="Broomfield"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">State <span style="font-weight:400;text-transform:none;letter-spacing:0;color:var(--rq-text-secondary);">(auto)</span></label><input type="text" class="rqd-modal__input" id="rqd-lead-state" placeholder="CO" maxlength="2" style="text-transform:uppercase;"></div>';
      html += '</div>';

      // Ship To — collapsed by default. Inline link toggle (no section divider
      // bar). When opened, all 9 ship-to fields stack same as sold-to.
      html += '<div style="margin:8px 0 6px;">';
      html += '<button type="button" id="rqd-lead-ship-toggle" style="background:none;border:none;color:var(--rq-blue);font-size:12px;font-weight:600;cursor:pointer;padding:0;font-family:inherit;">+ Add separate ship-to</button>';
      html += '</div>';
      html += '<div id="rqd-lead-ship-block" style="display:none;">';
      // Ship-To uses the same 2-col / 3-col compaction as Sold-To above.
      html += '<div class="rqd-modal__field-row rqd-modal__field-row--2col">';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Recipient Name</label><input type="text" class="rqd-modal__input" id="rqd-lead-ship-name" placeholder="Jane Doe"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Recipient Phone</label><input type="tel" class="rqd-modal__input" id="rqd-lead-ship-phone" placeholder="(555) 987-6543"></div>';
      html += '</div>';
      // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-08) — separate ship-to email (a distinct
      // receiving contact often has their own address; without this the ship-to email
      // silently defaulted to the Sold To email on the worker side).
      html += '<label class="rqd-modal__label">Recipient Email</label>';
      html += '<input type="email" class="rqd-modal__input" id="rqd-lead-ship-email" placeholder="jane@example.com">';
      html += '<label class="rqd-modal__label">Company</label>';
      html += '<input type="text" class="rqd-modal__input" id="rqd-lead-ship-company" placeholder="Receiving warehouse">';
      html += '<div class="rqd-modal__field-row rqd-modal__field-row--2col">';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Street 1</label><input type="text" class="rqd-modal__input" id="rqd-lead-ship-street1"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Street 2</label><input type="text" class="rqd-modal__input" id="rqd-lead-ship-street2"></div>';
      html += '</div>';
      html += '<div class="rqd-modal__field-row rqd-modal__field-row--3col">';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Zip Code</label><input type="text" class="rqd-modal__input" id="rqd-lead-ship-zip" maxlength="10"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">City</label><input type="text" class="rqd-modal__input" id="rqd-lead-ship-city"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">State</label><input type="text" class="rqd-modal__input" id="rqd-lead-ship-state" maxlength="2" style="text-transform:uppercase;"></div>';
      html += '</div>';
      html += '</div>';

      // Message — plain textarea, same shape as Pending modal's Note field
      html += '<label class="rqd-modal__label">Message / Notes</label>';
      html += '<textarea class="rqd-modal__input" id="rqd-lead-message" rows="4" placeholder="What did the customer say? What do they need?" style="resize:vertical;"></textarea>';

      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Referred By + % of GP capture at lead
         intake. Non-mandatory pair. When a name is set, it carries through
         the lead's lifetime → on quote creation from this lead, the
         Workstation auto-sets leadSource='shared' + populates Recipient
         Name + GP%. Name input pulls autocomplete from a shared localStorage
         memory that the Workstation also writes to (bsfs_referral_memory)
         so previously-used referral names suggest as admin types.
         On Sample Lead, this block is initially HIDDEN — gated on the
         Inquiry Channel = 'Referral' pick. The block reveals when that
         channel button is clicked. On Phone Lead + Email Lead, the block
         is always visible (no Inquiry Channel toggle on those forms). */
      var _refInitialDisplay = asSample ? 'none' : '';
      html += '<div id="rqd-lead-referral-block" style="display:' + _refInitialDisplay + ';">';
      /* Ryan 2026-05-16: Referred By + % of GP paired on one row.
         Dropped the parenthetical hints ("optional — leave blank...",
         "optional now — required when quoting...") — both fields are
         self-explanatory and the labels alone read cleaner. % of GP
         gets a narrower column since it's just a number. */
      html += '<div class="rqd-modal__field-row" style="grid-template-columns: 2fr 1fr; margin-top: 12px;">';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Referred By</label><input type="text" class="rqd-modal__input" id="rqd-lead-referral-name" list="rqd-lead-referral-datalist" maxlength="80" placeholder="Name of the person who referred this customer"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">% of GP</label><input type="number" class="rqd-modal__input" id="rqd-lead-referral-pct" min="0" max="100" step="0.5" placeholder="e.g. 10"></div>';
      html += '</div>';
      html += '<datalist id="rqd-lead-referral-datalist"></datalist>';
      html += '</div>';
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

      // Admin: assignee override at the bottom (least-frequent action lives last)
      if (isAdminUser) {
        html += '<label class="rqd-modal__label" style="margin-top:12px;">Assignee Override <span style="font-weight:400;text-transform:none;letter-spacing:0;color:var(--rq-text-secondary);">(force a specific rep)</span></label>';
        html += '<select class="rqd-modal__input" id="rqd-lead-assignee"><option value="">Auto (by territory)</option></select>';
      }

      modalBody.innerHTML = html;
      var _psc = { _isLeadCreate: true, _asSample: asSample };
      setPendingStatusChange(_psc);
      modalBackdrop.classList.add('rqd-modal-backdrop--open');
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — chip-style multi-email
         (hidden mirror keeps #rqd-lead-email.value = comma-joined for the
         existing submit collector). */
      var _leadEmailChips = (function() { var _e = document.getElementById('rqd-lead-email'); return (_e && window.RepPortal && window.RepPortal.installChipField) ? window.RepPortal.installChipField({ replace: _e, placeholder: 'john@example.com', ariaLabel: 'Customer email addresses' }) : null; })();

      // Sample channel toggle — single-select via active-class, value read at submit.
      if (asSample) {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — "Other" reveals the describe-it box. */
        (function () {
          var _t = document.getElementById('rqd-lead-project-type');
          var _w = document.getElementById('rqd-lead-project-type-other-wrap');
          if (_t && _w) _t.addEventListener('change', function () {
            _w.style.display = _t.value === 'Other' ? '' : 'none';
            if (_t.value === 'Other') { var _o = document.getElementById('rqd-lead-project-type-other'); if (_o) _o.focus(); }
          });
        })();
        var channelWrap = modalBody.querySelector('[data-sample-channel-toggle]');
        if (channelWrap) {
          channelWrap.querySelectorAll('[data-sample-channel]').forEach(function(btn) {
            btn.addEventListener('click', function() {
              channelWrap.querySelectorAll('[data-sample-channel]').forEach(function(b) {
                b.classList.remove('rqd-sample-channel-toggle__btn--active');
              });
              this.classList.add('rqd-sample-channel-toggle__btn--active');
              channelWrap.removeAttribute('data-sample-channel-error');
              /* CUSTOM_RFQPORTAL_BY_CLAUDE — Reveal / hide the Referred
                 By + % of GP block based on channel pick. 'referral' →
                 show; 'phone' / 'email' → hide and clear inputs so a
                 stale referral name doesn't sneak into a non-referral
                 sample lead. */
              var _ch = this.getAttribute('data-sample-channel');
              var _refBlock = document.getElementById('rqd-lead-referral-block');
              if (_refBlock) {
                if (_ch === 'referral') {
                  _refBlock.style.display = '';
                } else {
                  _refBlock.style.display = 'none';
                  var _nameEl = document.getElementById('rqd-lead-referral-name');
                  var _pctEl = document.getElementById('rqd-lead-referral-pct');
                  if (_nameEl) _nameEl.value = '';
                  if (_pctEl) _pctEl.value = '';
                }
              }
              /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
            });
          });
        }
      }

      // Live phone formatting (both contact phone + ship-to phone)
      function attachPhoneFormatter(id) {
        var el = document.getElementById(id);
        if (!el) return;
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-11) — delegate to the shared
           formatter so 10 digits + extension is handled identically to the QW and
           every other phone field (ext follows the deal through the pipeline). */
        if (window.RepPortal && window.RepPortal.bindPhoneInput) { window.RepPortal.bindPhoneInput(el); return; }
        // Fallback (shared portal JS not yet loaded): 10-digit main + extension.
        el.addEventListener('input', function() {
          var d = this.value.replace(/\D/g, '');
          var main = d.substring(0, 10), ext = d.substring(10), out;
          if (main.length === 0) out = '';
          else if (main.length <= 3) out = '(' + main;
          else if (main.length <= 6) out = '(' + main.substring(0,3) + ') ' + main.substring(3);
          else out = '(' + main.substring(0,3) + ') ' + main.substring(3,6) + '-' + main.substring(6);
          if (ext) out += ' ext. ' + ext;
          this.value = out;
        });
      }
      attachPhoneFormatter('rqd-lead-phone');
      attachPhoneFormatter('rqd-lead-ship-phone');

      // Zip → city + state auto-lookup
      var zipIn = document.getElementById('rqd-lead-zip');
      var stateIn = document.getElementById('rqd-lead-state');
      var cityIn = document.getElementById('rqd-lead-sold-city');
      var zipDebounce;
      if (zipIn) {
        zipIn.addEventListener('input', function() {
          var z = this.value.replace(/\D/g, '').substring(0, 5);
          this.value = z;
          clearTimeout(zipDebounce);
          if (z.length < 5) return;
          zipDebounce = setTimeout(function() {
            fetch('https://api.zippopotam.us/us/' + z)
              .then(function(r) { if (!r.ok) throw new Error(); return r.json(); })
              .then(function(data) {
                var p = data.places && data.places[0] || {};
                if (stateIn && p['state abbreviation'] && !stateIn.value) stateIn.value = p['state abbreviation'];
                if (cityIn && p['place name'] && !cityIn.value) cityIn.value = p['place name'];
              })
              .catch(function() {});
          }, 350);
        });
      }

      // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-08) — Ship-to zip → city + state
      // auto-lookup (mirrors the Sold To lookup above). Fills empty city/state only.
      var shipZipIn = document.getElementById('rqd-lead-ship-zip');
      var shipStateIn = document.getElementById('rqd-lead-ship-state');
      var shipCityIn = document.getElementById('rqd-lead-ship-city');
      var shipZipDebounce;
      if (shipZipIn) {
        shipZipIn.addEventListener('input', function() {
          var z = this.value.replace(/\D/g, '').substring(0, 5);
          this.value = z;
          clearTimeout(shipZipDebounce);
          if (z.length < 5) return;
          shipZipDebounce = setTimeout(function() {
            fetch('https://api.zippopotam.us/us/' + z)
              .then(function(r) { if (!r.ok) throw new Error(); return r.json(); })
              .then(function(data) {
                var p = data.places && data.places[0] || {};
                if (shipStateIn && p['state abbreviation'] && !shipStateIn.value) shipStateIn.value = p['state abbreviation'];
                if (shipCityIn && p['place name'] && !shipCityIn.value) shipCityIn.value = p['place name'];
              })
              .catch(function() {});
          }, 350);
        });
      }

      // Residential checkbox → auto-fill organization
      var resCb = document.getElementById('rqd-lead-residential');
      var orgIn = document.getElementById('rqd-lead-org');
      if (resCb && orgIn) {
        resCb.addEventListener('change', function() {
          if (this.checked) {
            if (!orgIn.value || orgIn.value.trim() === '') orgIn.value = 'n/a - residence';
          } else if (orgIn.value === 'n/a - residence') {
            orgIn.value = '';
          }
        });
      }

      // Ship To collapsible toggle
      var shipToggle = document.getElementById('rqd-lead-ship-toggle');
      var shipBlock = document.getElementById('rqd-lead-ship-block');
      if (shipToggle && shipBlock) {
        shipToggle.addEventListener('click', function() {
          var hidden = shipBlock.style.display === 'none';
          shipBlock.style.display = hidden ? 'block' : 'none';
          shipToggle.textContent = hidden ? '— Hide ship-to' : '+ Add separate ship-to';
        });
      }

      // (Rich-text toolbar dropped — message field is now a plain <textarea>
      // matching the Pending modal. Worker still sanitizes any HTML in case
      // the rep pastes something rich.)

      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Populate the Referred By datalist
         from the shared localStorage memory so prior referral names
         autocomplete as admin types. Memory is written when this form
         submits with a name AND when the Workstation saves a quote with
         a referral name set (same `bsfs_referral_memory` key — both
         surfaces stay in sync). */
      try {
        var _refDatalist = document.getElementById('rqd-lead-referral-datalist');
        if (_refDatalist) {
          var _mem = JSON.parse(localStorage.getItem('bsfs_referral_memory') || '[]') || [];
          if (Array.isArray(_mem)) {
            _mem.forEach(function(m) {
              if (m && m.name) {
                var opt = document.createElement('option');
                opt.value = m.name;
                _refDatalist.appendChild(opt);
              }
            });
          }
        }
      } catch (_) { /* localStorage disabled — datalist stays empty, name input still typeable */ }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

      // Populate assignee override dropdown (admin only)
      if (isAdminUser) {
        var sel = document.getElementById('rqd-lead-assignee');
        if (sel) {
          /* CUSTOM_RFQPORTAL_BY_CLAUDE — Add the logged-in admin as a
             selectable option FIRST. /rep-list-basic excludes the
             requesting rep server-side (defensive default for share /
             reassign pickers where you almost never want to pick
             yourself). On the +New lead form admin DOES want to be
             selectable — they may be the one taking the call. Inject
             self from the session before fetching the rest. */
          var _selfParts = (sess.fullName || '').split(/\s+/).filter(Boolean);
          var _selfShort = _selfParts.length
            ? (_selfParts[0] + (_selfParts.length > 1 ? ' ' + _selfParts[_selfParts.length - 1][0] + '.' : '')).trim()
            : sess.code;
          var selfOpt = document.createElement('option');
          selfOpt.value = sess.code;
          selfOpt.textContent = _selfShort + ' (' + sess.code + ')';
          sel.appendChild(selfOpt);
          /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
          window.RepPortal.API.get('/rep-list-basic').then(function(resp) {
            var reps = (resp && resp.reps) || [];
            reps.forEach(function(r) {
              var opt = document.createElement('option');
              opt.value = r.code;
              opt.textContent = r.name + ' (' + r.code + ')';
              sel.appendChild(opt);
            });
          }).catch(function() {});
        }
      }

      // Prefill from AI extraction (Email Lead flow). Drops every field
      // the AI returned into the corresponding input. Empty / missing
      // fields are left blank for the rep to fill manually.
      if (opts && opts.prefill) {
        var p = opts.prefill;
        function setVal(id, val) {
          var el = document.getElementById(id);
          if (el && val != null && String(val).trim() !== '') el.value = String(val).trim();
        }
        setVal('rqd-lead-name', p.name);
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-25) — the email field is a
           chip field since 2026-06-19: #rqd-lead-email is its HIDDEN mirror, so
           setVal wrote there and no chip ever rendered (the field looked blank
           after every AI extraction). Seed the chips through the field's own API;
           setVal stays only as the fallback when the chip field isn't installed. */
        if (_leadEmailChips && p.email) {
          _leadEmailChips.setChips(String(p.email).split(/[,;\s]+/));
        } else {
          setVal('rqd-lead-email', p.email);
        }
        setVal('rqd-lead-phone', p.phone);
        setVal('rqd-lead-org', p.organization || p.company);
        setVal('rqd-lead-sold-street1', p.street || p.street1);
        setVal('rqd-lead-sold-street2', p.street2);
        setVal('rqd-lead-zip', p.zip);
        setVal('rqd-lead-sold-city', p.city);
        setVal('rqd-lead-state', p.state);
        setVal('rqd-lead-message', p.message);
        // Trip the residential checkbox if AI flagged it (or the org
        // came back as the residential placeholder).
        if (p.residential || /residence/i.test(p.organization || '')) {
          var resCbAi = document.getElementById('rqd-lead-residential');
          if (resCbAi) resCbAi.checked = true;
        }
        // Mark the modal so we know the lead came in via the email
        // pipeline — surfaces source='email-lead' on the saved record.
        _psc._fromEmailLead = true;
        _psc._originalEmail = opts.originalEmail || '';
        // "← Re-process raw email" link at the top of the form so the
        // rep can edit the original paste + run extraction again
        // without canceling out and starting fresh. Clicking it
        // re-opens step 1 with the raw text preserved.
        var reprocessRow = document.createElement('div');
        reprocessRow.style.cssText = 'margin:0 0 12px;font-size:12px;';
        reprocessRow.innerHTML = '<button type="button" id="rqd-email-reprocess" style="background:none;border:none;color:var(--rq-blue);font-weight:600;cursor:pointer;padding:0;font-family:inherit;font-size:12px;">← Re-process raw email</button>';
        modalBody.insertBefore(reprocessRow, modalBody.firstChild);
        var reprocessBtn = document.getElementById('rqd-email-reprocess');
        if (reprocessBtn) {
          reprocessBtn.addEventListener('click', function () {
            var stash = opts.originalEmail || '';
            window.__openNewEmailLead(null, { initialRaw: stash });
          });
        }
      }
    };

    // ── + New Sample wrapper ── thin wrapper that opens the same lead modal
    // but with asSample=true so the submit lands as source='sample-request'.
    window.__openNewSample = function(e) {
      window.__openNewLead(e, { asSample: true });
    };

    // ── + New Email Lead ── two-step flow:
    //   1. Paste raw email content into a textarea, click Process.
    //   2. Worker calls Claude API to extract structured fields.
    //   3. The standard New Lead modal opens, pre-filled with whatever
    //      the AI extracted. Rep edits + Confirms to save the lead
    //      through the same /lead-intake pipeline as Phone Lead.
    // Lower-friction failsafe so leads-via-email don't slip through the
    // cracks while a rep is forwarding to handle them.
    window.__openNewEmailLead = function(e, opts) {
      if (e) e.stopPropagation();
      var menu = document.getElementById('rqd-new-menu');
      if (menu) menu.style.display = 'none';
      var sess = window.RepPortal && window.RepPortal.Auth.getSession();
      if (sess && (sess.role === 'cfa')) return;
      var initialRaw = (opts && opts.initialRaw) || '';
      _ensureModalEls();

      modalTitle.textContent = 'Email Lead';
      modalSub.textContent = 'Paste the raw email — AI extracts the contact fields';
      modalEl.classList.add('rqd-modal--wide');

      var html = '';
      html += '<label class="rqd-modal__label">Paste email content</label>';
      html += '<textarea class="rqd-modal__input" id="rqd-email-lead-raw" rows="14" placeholder="From: jane@acme.com\nSent: Tuesday, May 7, 2026 2:14 PM\nTo: ryan@blueskyfitsupply.com\nSubject: Need flooring for our gym\n\nHi Ryan,\n\nWe&apos;re building a 2,400 sq ft training facility in Boulder...\n\n— Jane Doe\nAcme Fitness Center\n(720) 555-1234\n123 Main St, Boulder, CO 80302" style="resize:vertical;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:12px;line-height:1.45;"></textarea>';
      html += '<div id="rqd-email-lead-status" style="font-size:12px;color:var(--rq-text-secondary);margin-top:8px;min-height:18px;"></div>';

      modalBody.innerHTML = html;
      // Repurpose the standard footer Confirm as "Process with AI" for
      // step 1. The footer click handler (modalConfirm listener) checks
      // for _isEmailLeadStep1 and fires the extraction instead of saving
      // a lead. Standard Cancel works as-is. After Process succeeds,
      // __openNewLead is called which resets pendingStatusChange so the
      // footer button reverts to its normal "Confirm" save behavior.
      setPendingStatusChange({ _isEmailLeadStep1: true });
      modalConfirm.textContent = 'Process with AI';
      modalConfirm.disabled = false;
      modalBackdrop.classList.add('rqd-modal-backdrop--open');

      // Auto-focus the textarea so the rep can paste immediately. If
      // we got here via "← Re-process raw email" from step 2, restore
      // the previous paste so they can edit + re-extract.
      setTimeout(function () {
        var rawEl = document.getElementById('rqd-email-lead-raw');
        if (rawEl) {
          if (initialRaw) rawEl.value = initialRaw;
          rawEl.focus();
        }
      }, 50);
      // (The actual Process logic lives in the modalConfirm click
      //  handler — see the _isEmailLeadStep1 branch there. Keeping it
      //  centralized so all modal-footer button work flows through the
      //  same dispatcher.)
    };

    // ── New Pending ──

    window.__openNewPending = function(e) {
      if (e) e.stopPropagation();
      var sess = window.RepPortal && window.RepPortal.Auth.getSession();
      if (sess && (sess.role === 'cfa')) return;
      var isAdminUser = sess && sess.admin;
      _ensureModalEls();
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — "New Pending Quote"
         → "New Pending Deal". Reps think in "deals" — quote is the artifact
         this becomes once it's built, but at creation time it's just a
         reminder of an opportunity. */
      modalTitle.textContent = 'New Pending Deal';
      modalSub.textContent = 'Quick-save a reminder to create a quote later';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Reset the
         modalConfirm label to "Create". Email Lead flow leaves it as
         "Process with AI" — without this reset, pending creation
         inherits the stale label. */
      modalConfirm.textContent = 'Create';
      modalConfirm.disabled = false;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — match the wide/compact
         layout of the New Lead / Sample / Phone + SOLD TO modals (720px, tighter
         input spacing, 2-col rows have room) so the form fits without a scroll.
         This was the only +New modal missing the class. */
      modalEl.classList.add('rqd-modal--wide');
      var html = '';
      html += '<label class="rqd-modal__label">Customer Name *</label>';
      html += '<input type="text" class="rqd-modal__input" id="rqd-pending-name" placeholder="John Smith">';
      html += '<label class="rqd-modal__label">Organization</label>';
      html += '<input type="text" class="rqd-modal__input" id="rqd-pending-org" placeholder="Acme Fitness">';
      /* Ryan 2026-05-16: Email + Phone paired on one row (same
         compaction as the New Lead modal). */
      html += '<div class="rqd-modal__field-row rqd-modal__field-row--2col">';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Email (comma separated)</label><input type="email" class="rqd-modal__input" id="rqd-pending-email" placeholder="john@example.com"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Phone</label><input type="tel" class="rqd-modal__input" id="rqd-pending-phone" placeholder="(555) 123-4567"></div>';
      html += '</div>';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — full address (matches the
         New Lead / QW customer card): Street 1/2 + Zip/City/State, with zip→
         city/state auto-lookup. The /create-pending worker already stores these. */
      html += '<div class="rqd-modal__field-row rqd-modal__field-row--2col">';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Street 1</label><input type="text" class="rqd-modal__input" id="rqd-pending-street1" placeholder="123 Main St"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Street 2</label><input type="text" class="rqd-modal__input" id="rqd-pending-street2" placeholder="Suite 200"></div>';
      html += '</div>';
      html += '<div class="rqd-modal__field-row rqd-modal__field-row--3col">';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Zip Code</label><input type="text" class="rqd-modal__input" id="rqd-pending-zip" placeholder="80021" maxlength="10"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">City <span style="font-weight:400;text-transform:none;letter-spacing:0;color:var(--rq-text-secondary);">(auto)</span></label><input type="text" class="rqd-modal__input" id="rqd-pending-city" placeholder="Broomfield"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">State <span style="font-weight:400;text-transform:none;letter-spacing:0;color:var(--rq-text-secondary);">(auto)</span></label><input type="text" class="rqd-modal__input" id="rqd-pending-state" placeholder="CO" maxlength="2" style="text-transform:uppercase;"></div>';
      html += '</div>';
      html += '<label class="rqd-modal__label">Note *</label>';
      html += '<textarea class="rqd-modal__input" id="rqd-pending-note" rows="3" placeholder="Quick notes about what they need..." style="resize:vertical;"></textarea>';
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-07-24, DNI decoupling piece 3) — the "How did they
         reach us?" select was REMOVED. It was the THIRD door onto wm.leadContext.source (the
         pending's stamped source rode onto the quote at build time), so leaving it would have kept
         a rep pick deciding marketing origin after the Quote Workstation + Terms modal copies were
         retired. Origin is now derived from hard signals server-side (lib/lead-signals.js). */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-12) — the referral partner block SURVIVES: it
         carries a % of GP, so it is a COMMISSION field (the paid Shared Deal recipient), not a
         marketing source. It used to slide open only when the retired select read "Referral"; it is
         now always shown. The name autocompletes from the shared bsfs_referral_memory; both values
         persist on the pending and prefill the Workstation's Shared Deal recipient at quote time. */
      html += '<label class="rqd-modal__label" style="margin-top:12px;">Referral partner <span style="font-weight:400;text-transform:none;letter-spacing:0;color:var(--rq-text-secondary);">(optional — paid a share of GP)</span></label>';
      html += '<div id="rqd-pending-referral-block">';
      html += '<div class="rqd-modal__field-row" style="grid-template-columns: 2fr 1fr; margin-top: 10px;">';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">Referred By</label><input type="text" class="rqd-modal__input" id="rqd-pending-referral-name" list="rqd-pending-referral-datalist" maxlength="80" placeholder="Who referred this customer?"></div>';
      html +=   '<div class="rqd-modal__field"><label class="rqd-modal__label">% of GP</label><input type="number" class="rqd-modal__input" id="rqd-pending-referral-pct" min="0" max="100" step="0.5" placeholder="e.g. 10"></div>';
      html += '</div>';
      html += '<datalist id="rqd-pending-referral-datalist"></datalist>';
      html += '</div>';
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Follow-up on pending
         creation is now MANDATORY. Date picker + preset shelf (+7/+14/+30/
         +60/+90/+180). Rep must pick a wake date so every pending deal
         has a scheduled follow-up baked in — no orphaned pendings that
         get forgotten about. Submits as followUpAt in the /create-pending
         payload; worker writes pending.followUp. Validation enforced
         in the submit handler. */
      html += '<label class="rqd-modal__label">Follow-Up *</label>';
      html += '<input type="date" class="rqd-modal__input" id="rqd-pending-followup" aria-label="Follow-up date" style="margin-bottom:8px;">';
      html += '<div class="rqd-pending-followup-presets" style="display:flex;gap:6px;flex-wrap:wrap;">';
      [7, 14, 30, 60, 90, 180].forEach(function(d) {
        html += '<button type="button" class="rqd-pending-followup-preset rqd-notes-log__followup-pick" data-days="' + d + '" style="flex:1;min-width:48px;">+' + d + 'd</button>';
      });
      html += '</div>';
      if (isAdminUser) {
        html += '<label class="rqd-modal__label" style="margin-top:12px;">Assignee <span style="font-weight:400;text-transform:none;letter-spacing:0;color:var(--rq-text-secondary);">(defaults to you)</span></label>';
        html += '<select class="rqd-modal__input" id="rqd-pending-assignee"><option value="">Me</option></select>';
      }
      modalBody.innerHTML = html;
      setPendingStatusChange({ _isPendingCreate: true });
      modalBackdrop.classList.add('rqd-modal-backdrop--open');
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — chip-style multi-email
         (hidden mirror keeps #rqd-pending-email.value = comma-joined). */
      var _npEmailChips = (function() { var _e = document.getElementById('rqd-pending-email'); return (_e && window.RepPortal && window.RepPortal.installChipField) ? window.RepPortal.installChipField({ replace: _e, placeholder: 'john@example.com', ariaLabel: 'Customer email addresses' }) : null; })();
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — customer-history typeahead
         on Organization + Name (same as the Quote Workstation): pick a past
         customer → auto-fill the rest of their contact. */
      (function() {
        function fmtPhone(p) { return (p && window.RepPortal && window.RepPortal.formatPhone) ? window.RepPortal.formatPhone(p) : (p || ''); }
        function fillFrom(m) {
          if (!m) return;
          var set = function(id, v) { var el = document.getElementById(id); if (el && v != null) el.value = v; };
          var setIf = function(id, v) { var el = document.getElementById(id); if (el && v && !el.value.trim()) el.value = v; };
          /* The picked entry can lack a street even when city/state are set (e.g.
             a customer quick-created from just a zip). Fall back to a richer
             entry for the same email, else same company, that DOES carry a
             street so the address fills fully. */
          var addr = m;
          if (!m.street) {
            var cache = _custLookupCache || [];
            var alt = (m.email && cache.find(function(c) { return c && c.email === m.email && c.street; }))
                   || (m.company && cache.find(function(c) { return c && c.company && c.company.toLowerCase() === m.company.toLowerCase() && c.street; }));
            if (alt) addr = alt;
          }
          if (m.name) set('rqd-pending-name', m.name);
          if (m.company) set('rqd-pending-org', m.company);
          setIf('rqd-pending-phone', m.phone ? fmtPhone(m.phone) : '');
          setIf('rqd-pending-street1', addr.street);
          setIf('rqd-pending-street2', addr.street2);
          setIf('rqd-pending-zip', addr.zip || m.zip);
          setIf('rqd-pending-city', addr.city || m.city);
          setIf('rqd-pending-state', addr.state || m.state);
          if (_npEmailChips && m.email && _npEmailChips.getChips().length === 0) _npEmailChips.add(m.email);
        }
        _bindCustomerLookup(document.getElementById('rqd-pending-org'), 'company', fillFrom);
        _bindCustomerLookup(document.getElementById('rqd-pending-name'), 'name', fillFrom);
      })();
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-19) — zip → city/state auto-lookup
         (mirrors the New Lead modal). Fills empty city/state only. */
      (function() {
        var zipIn = document.getElementById('rqd-pending-zip');
        var cityIn = document.getElementById('rqd-pending-city');
        var stateIn = document.getElementById('rqd-pending-state');
        var zipDebounce;
        if (!zipIn) return;
        zipIn.addEventListener('input', function() {
          var z = this.value.replace(/\D/g, '').substring(0, 5);
          this.value = z;
          clearTimeout(zipDebounce);
          if (z.length < 5) return;
          zipDebounce = setTimeout(function() {
            fetch('https://api.zippopotam.us/us/' + z)
              .then(function(r) { if (!r.ok) throw new Error(); return r.json(); })
              .then(function(data) {
                var p = data.places && data.places[0] || {};
                if (stateIn && p['state abbreviation'] && !stateIn.value) stateIn.value = p['state abbreviation'];
                if (cityIn && p['place name'] && !cityIn.value) cityIn.value = p['place name'];
              })
              .catch(function() {});
          }, 350);
        });
      })();
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-12) — Referral slide-down
         wiring: source=Referral slides the block open; switching away slides
         it shut and clears the inputs so a stale partner never rides along.
         Datalist hydrates from the shared memory ({name, gpPercent} entries —
         same shape the Workstation + New Lead form read/write). Picking a
         remembered name auto-fills its last-used GP% if the field is empty. */
      var pRefBlock = document.getElementById('rqd-pending-referral-block');
      if (pRefBlock) {
        try {
          var _pDl = document.getElementById('rqd-pending-referral-datalist');
          var _pMem = JSON.parse(localStorage.getItem('bsfs_referral_memory') || '[]') || [];
          if (_pDl && Array.isArray(_pMem)) {
            _pMem.forEach(function(m) {
              if (m && m.name) {
                var opt = document.createElement('option');
                opt.value = m.name;
                _pDl.appendChild(opt);
              }
            });
          }
          var _pNameIn = document.getElementById('rqd-pending-referral-name');
          if (_pNameIn) {
            _pNameIn.addEventListener('change', function() {
              var _pctIn = document.getElementById('rqd-pending-referral-pct');
              if (!_pctIn || _pctIn.value) return;
              var hit = (_pMem || []).find(function(m) { return m && m.name && String(m.name).toLowerCase() === _pNameIn.value.trim().toLowerCase(); });
              if (hit && hit.gpPercent > 0) _pctIn.value = hit.gpPercent;
            });
          }
        } catch (_) { /* localStorage disabled — typing still works */ }
        // The slide-open/clear handler went with the retired source select — the referral block is
        // always visible now, and an empty name simply means "no referral partner".
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      // Live phone formatting (10 digits + extension) — shared formatter.
      var phoneIn = document.getElementById('rqd-pending-phone');
      if (phoneIn) {
        if (window.RepPortal && window.RepPortal.bindPhoneInput) {
          window.RepPortal.bindPhoneInput(phoneIn);
        } else {
          phoneIn.addEventListener('input', function() {
            var d = this.value.replace(/\D/g, '');
            var main = d.substring(0, 10), ext = d.substring(10), out;
            if (main.length === 0) out = '';
            else if (main.length <= 3) out = '(' + main;
            else if (main.length <= 6) out = '(' + main.substring(0,3) + ') ' + main.substring(3);
            else out = '(' + main.substring(0,3) + ') ' + main.substring(3,6) + '-' + main.substring(6);
            if (ext) out += ' ext. ' + ext;
            this.value = out;
          });
        }
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Wire up preset
         clicks to sync the date input + highlight the active preset.
         Re-clicking the active preset clears the follow-up entirely. */
      var followUpInput = document.getElementById('rqd-pending-followup');
      var presetButtons = document.querySelectorAll('.rqd-pending-followup-preset');
      presetButtons.forEach(function(btn) {
        btn.addEventListener('click', function() {
          var days = parseInt(btn.dataset.days, 10);
          if (!isFinite(days)) return;
          var isAlreadyActive = btn.classList.contains('rqd-notes-log__followup-pick--active');
          presetButtons.forEach(function(b) { b.classList.remove('rqd-notes-log__followup-pick--active'); });
          if (isAlreadyActive) {
            // Re-click clears
            if (followUpInput) followUpInput.value = '';
          } else {
            btn.classList.add('rqd-notes-log__followup-pick--active');
            if (followUpInput) followUpInput.value = daysFromNowYmd(days);
          }
        });
      });
      // Typing into the date input clears preset highlights (custom date
      // doesn't necessarily match a preset).
      if (followUpInput) {
        followUpInput.addEventListener('input', function() {
          if (!followUpInput.value) {
            presetButtons.forEach(function(b) { b.classList.remove('rqd-notes-log__followup-pick--active'); });
            return;
          }
          var snapped = snapYmdToWeekday(followUpInput.value);
          if (snapped !== followUpInput.value) followUpInput.value = snapped;
          // Highlight matching preset if the typed date lines up with one.
          presetButtons.forEach(function(b) {
            var d = parseInt(b.dataset.days, 10);
            b.classList.toggle('rqd-notes-log__followup-pick--active', isFinite(d) && daysFromNowYmd(d) === snapped);
          });
        });
      }
      // Populate assignee dropdown (admin only)
      if (isAdminUser) {
        var sel = document.getElementById('rqd-pending-assignee');
        if (sel) {
          window.RepPortal.API.get('/rep-list-basic').then(function(resp) {
            var reps = (resp && resp.reps) || [];
            reps.forEach(function(r) {
              var opt = document.createElement('option');
              opt.value = r.code;
              opt.textContent = r.name + ' (' + r.code + ')';
              sel.appendChild(opt);
            });
          }).catch(function() {});
        }
      }
    };

  // === Init — main.js calls this once at boot to inject deps ===
  window.RQD.initLeadCreate = function(d) {
    d = d || {};
    daysFromNowYmd = d.daysFromNowYmd;
    snapYmdToWeekday = d.snapYmdToWeekday;
    API = d.API || (window.RepPortal && window.RepPortal.API);
    Auth = d.Auth || (window.RepPortal && window.RepPortal.Auth);
    setPendingStatusChange = d.setPendingStatusChange;
  };
})();
